import React, { useState, useMemo } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Paper,
  Grid,
  Divider,
  Button,
  TextField,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DrawIcon from '@mui/icons-material/Draw';
import api from '../../services/api';
import DigitalSignatureCapture from '../signatures/DigitalSignatureCapture';
import DocumentHeader from '../documents/DocumentHeader';
import DocumentFooter from '../documents/DocumentFooter';
import LegalClausesSection from '../documents/LegalClausesSection';
import {
  renderMarkdown,
  SectionWrapper,
  SectionHeader,
  FieldLabel,
  CardField,
  DOCUMENT_COLORS,
  APPBAR_GRADIENT,
} from '../documents/documentStyles';

// ── Section icon map ─────────────────────────────────────────────────────────
const SECTION_ICONS = {
  overview: <InfoOutlinedIcon fontSize="small" />,
  scope: <BuildOutlinedIcon fontSize="small" />,
  timeline: <ScheduleOutlinedIcon fontSize="small" />,
  selection: <ChecklistOutlinedIcon fontSize="small" />,
  documentation: <FolderOutlinedIcon fontSize="small" />,
  proposalReqs: <AssignmentOutlinedIcon fontSize="small" />,
  terms: <GavelOutlinedIcon fontSize="small" />,
};

const ContractorHireDocumentPreview = ({ open, onClose, data, onSubmit }) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [improvingField, setImprovingField] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [lightboxImg, setLightboxImg] = useState(null);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);

  const handleSendClick = () => {
    setShowSignatureDialog(true);
  };

  const handleSignatureComplete = (signatureData) => {
    setShowSignatureDialog(false);
    onSubmit({ ...data, ...editedData, signature: signatureData });
  };

  // Create object URLs for uploaded files (memoized to avoid re-creating)
  const filePreviewUrls = useMemo(() => {
    if (!data.uploadedFiles || data.uploadedFiles.length === 0) return [];
    return data.uploadedFiles.map((file) => ({
      url: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
      isImage: file.type?.startsWith('image/'),
    }));
  }, [data.uploadedFiles]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImproveWithAI = async (field) => {
    const currentValue = editedData[field] || data[field] || '';
    if (!currentValue.trim()) {
      showSnackbar('Please enter some content first', 'warning');
      return;
    }

    setImprovingField(field);
    try {
      const response = await api.post('/bids/ai/improve-section', {
        section: field,
        currentContent: currentValue,
        improvementRequest: 'Make this more professional and detailed for a contractor quote request document'
      });

      setEditedData({ ...editedData, [field]: response.data.data.improvedContent });
      showSnackbar('Content improved with AI!', 'success');
    } catch (error) {
      console.error('Error improving content:', error);
      showSnackbar('Failed to improve content. Please try again.', 'error');
    } finally {
      setImprovingField(null);
    }
  };

  const handleStartEdit = (section) => {
    setEditingSection(section);
    setEditedData({ ...data });
  };

  const handleSaveEdit = () => {
    Object.assign(data, editedData);
    setEditingSection(null);
  };

  const handleCancelEdit = () => {
    setEditedData({});
    setEditingSection(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (date) => {
    if (!date) return 'TBD';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ── Editable text (edit mode vs. display mode) ────────────────────────────
  const renderEditableText = (section, field, value, multiline = false) => {
    const isEditing = editingSection === section;

    if (isEditing) {
      return (
        <Box>
          <TextField
            fullWidth
            multiline={multiline}
            rows={multiline ? 4 : 1}
            value={editedData[field] || value || ''}
            onChange={(e) => setEditedData({ ...editedData, [field]: e.target.value })}
            variant="outlined"
            size="small"
          />
          {multiline && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Enhance this text with AI">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={improvingField === field ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                  onClick={() => handleImproveWithAI(field)}
                  disabled={improvingField !== null || !editedData[field]}
                  sx={{
                    textTransform: 'none',
                    borderColor: DOCUMENT_COLORS.brand,
                    color: DOCUMENT_COLORS.brand,
                    '&:hover': {
                      borderColor: DOCUMENT_COLORS.brandDark,
                      backgroundColor: 'rgba(37, 99, 235, 0.04)'
                    }
                  }}
                >
                  {improvingField === field ? 'Improving...' : 'Improve with AI'}
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
      );
    }

    // Display mode: use markdown renderer for multiline, plain text for single-line
    if (multiline && value) {
      return (
        <Box
          sx={{ cursor: 'pointer', minHeight: '24px' }}
          onClick={() => handleStartEdit(section)}
        >
          {renderMarkdown(value)}
        </Box>
      );
    }

    return (
      <Typography
        variant="body1"
        sx={{ cursor: 'pointer', minHeight: '24px', fontWeight: 500 }}
        onClick={() => handleStartEdit(section)}
      >
        {value || 'Click to edit'}
      </Typography>
    );
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { background: DOCUMENT_COLORS.dialogBg }
      }}
    >
      <AppBar position="sticky" elevation={0} sx={{ background: APPBAR_GRADIENT }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, ml: 2, fontWeight: 600 }}>
            Request for Quotation - {data.projectTitle || 'Untitled Project'}
          </Typography>
          <Button
            color="inherit"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mr: 1, textTransform: 'none' }}
          >
            Print
          </Button>
          <Button
            color="inherit"
            startIcon={<DownloadIcon />}
            sx={{ mr: 1, textTransform: 'none' }}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            sx={{
              background: '#fff',
              color: DOCUMENT_COLORS.brand,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { background: '#F0F4FF' }
            }}
            onClick={handleSendClick}
            startIcon={<DrawIcon />}
          >
            Sign & Send to Contractors
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>

          {/* Document Header */}
          <DocumentHeader
            documentType="REQUEST FOR QUOTATION"
            issuer={{
              name: data.clientName,
            }}
            date={formatDate(new Date())}
            meta={{ 'Response Deadline': formatDate(data.responseDeadline) }}
          />

          <Divider sx={{ mb: 4 }} />

          {/* ── Section 1: Project Overview ── */}
          <SectionWrapper>
            <SectionHeader
              icon={SECTION_ICONS.overview}
              title="1. Project Overview"
              editing={editingSection === 'overview'}
              onEdit={() => handleStartEdit('overview')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FieldLabel>Project Title</FieldLabel>
                {renderEditableText('overview', 'projectTitle', data.projectTitle)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Work Type</FieldLabel>
                  {renderEditableText('overview', 'workType', data.workType)}
                </CardField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Location</FieldLabel>
                  {renderEditableText('overview', 'location', data.location)}
                </CardField>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Project Summary</FieldLabel>
                {renderEditableText('overview', 'projectSummary', data.projectSummary, true)}
              </Grid>
            </Grid>
          </SectionWrapper>

          {/* ── Section 2: Scope & Requirements ── */}
          <SectionWrapper>
            <SectionHeader
              icon={SECTION_ICONS.scope}
              title="2. Scope of Work & Requirements"
              editing={editingSection === 'scope'}
              onEdit={() => handleStartEdit('scope')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <FieldLabel>Detailed Description</FieldLabel>
            {renderEditableText('scope', 'detailedDescription', data.detailedDescription, true)}
            <Divider sx={{ my: 2 }} />
            <FieldLabel>Key Requirements</FieldLabel>
            {renderEditableText('scope', 'keyRequirements', data.keyRequirements, true)}
            <Divider sx={{ my: 2 }} />
            <FieldLabel>Quality Standards</FieldLabel>
            {renderEditableText('scope', 'qualityStandards', data.qualityStandards, true)}
          </SectionWrapper>

          {/* ── Section 3: Timeline ── */}
          <SectionWrapper>
            <SectionHeader
              icon={SECTION_ICONS.timeline}
              title="3. Project Timeline"
              editing={editingSection === 'timeline'}
              onEdit={() => handleStartEdit('timeline')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Project Start Date</FieldLabel>
                  {renderEditableText('timeline', 'startDate', formatDate(data.startDate))}
                </CardField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Expected Duration</FieldLabel>
                  {renderEditableText('timeline', 'duration', data.duration)}
                </CardField>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Key Milestones</FieldLabel>
                {renderEditableText('timeline', 'milestones', data.milestones, true)}
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Timeline Urgency</FieldLabel>
                <Chip
                  label={data.timelineUrgency || 'Standard'}
                  color={data.timelineUrgency === 'Urgent' ? 'error' : 'primary'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>
            </Grid>
          </SectionWrapper>

          {/* ── Section 4: Selection Criteria ── */}
          <SectionWrapper>
            <SectionHeader
              icon={SECTION_ICONS.selection}
              title="4. Selection Criteria & Evaluation"
              editing={editingSection === 'selection'}
              onEdit={() => handleStartEdit('selection')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <CardField>
              <FieldLabel>Number of Contractors to Hire</FieldLabel>
              {renderEditableText('selection', 'numberOfContractors', data.numberOfContractors?.toString() || '1')}
            </CardField>
            <Box sx={{ mt: 2 }}>
              <FieldLabel>Evaluation Criteria (in order of importance)</FieldLabel>
              {renderEditableText('selection', 'evaluationCriteria', data.evaluationCriteria, true)}
            </Box>
            <Divider sx={{ my: 2 }} />
            <FieldLabel>Required Qualifications</FieldLabel>
            {renderEditableText('selection', 'requiredQualifications', data.requiredQualifications, true)}
          </SectionWrapper>

          {/* ── Section 5: Supporting Documentation ── */}
          <SectionWrapper>
            <SectionHeader
              icon={SECTION_ICONS.documentation}
              title="5. Supporting Documentation"
              editing={editingSection === 'documentation'}
              onEdit={() => handleStartEdit('documentation')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <FieldLabel>Documents Provided</FieldLabel>
            {renderEditableText('documentation', 'documentsProvided', data.documentsProvided, true)}

            {/* Embedded photo thumbnails */}
            {filePreviewUrls.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <FieldLabel>Attached Files</FieldLabel>

                {/* Image thumbnails grid */}
                {filePreviewUrls.some(f => f.isImage) && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {filePreviewUrls.filter(f => f.isImage).map((file, index) => (
                      <Grid item xs={6} sm={4} key={index}>
                        <Box
                          onClick={() => setLightboxImg(file.url)}
                          sx={{
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `2px solid ${DOCUMENT_COLORS.border}`,
                            cursor: 'pointer',
                            transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              borderColor: DOCUMENT_COLORS.brand,
                              transform: 'scale(1.02)',
                              boxShadow: '0 4px 12px rgba(37,99,235,0.15)',
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={file.url}
                            alt={file.name}
                            sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                          />
                          <Box sx={{ px: 1.5, py: 1, bgcolor: '#F8FAFC', borderTop: `1px solid ${DOCUMENT_COLORS.border}` }}>
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569', fontWeight: 500 }}
                            >
                              {file.name}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {/* Non-image files as chips */}
                {filePreviewUrls.some(f => !f.isImage) && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {filePreviewUrls.filter(f => !f.isImage).map((file, index) => (
                      <Chip
                        key={index}
                        icon={<AttachFileIcon />}
                        label={file.name}
                        variant="outlined"
                        sx={{ maxWidth: 250, fontWeight: 500 }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <FieldLabel>Additional Information Available</FieldLabel>
              {renderEditableText('documentation', 'additionalInfo', data.additionalInfo, true)}
            </Box>
          </SectionWrapper>

          {/* ── Section 6: Proposal Requirements ── */}
          <SectionWrapper>
            <SectionHeader icon={SECTION_ICONS.proposalReqs} title="6. Quotation Requirements" />
            <Typography variant="body2" sx={{ mb: 1.5, color: '#475569' }}>
              Contractors responding to this RFP must provide:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2.5, m: 0,
                '& li': { mb: 1, color: '#334155', lineHeight: 1.7, '&::marker': { color: DOCUMENT_COLORS.brand } },
              }}
            >
              <Typography component="li" variant="body2">Detailed project approach and methodology</Typography>
              <Typography component="li" variant="body2">Itemized cost breakdown</Typography>
              <Typography component="li" variant="body2">Proposed timeline with milestones</Typography>
              <Typography component="li" variant="body2">Team qualifications and relevant experience</Typography>
              <Typography component="li" variant="body2">References from similar projects</Typography>
              <Typography component="li" variant="body2">Proof of licensing and insurance</Typography>
              <Typography component="li" variant="body2">Warranty and guarantee information</Typography>
            </Box>
          </SectionWrapper>

          {/* ── Section 7: Legal Terms & Conditions ── */}
          <LegalClausesSection
            documentType="contractor_hire"
            sectionNumber={7}
            additionalTerms={data.termsAndConditions}
          />

          <DocumentFooter documentType="request for quotation" />
        </Paper>
      </Container>

      {/* ── Lightbox dialog for full-size image ── */}
      <Dialog
        open={!!lightboxImg}
        onClose={() => setLightboxImg(null)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
      >
        {lightboxImg && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setLightboxImg(null)}
              sx={{
                position: 'absolute', top: -40, right: 0,
                color: '#fff', bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="img"
              src={lightboxImg}
              alt="Full size preview"
              sx={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 2 }}
            />
          </Box>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      {/* Digital Signature Capture */}
      <DigitalSignatureCapture
        open={showSignatureDialog}
        onClose={() => setShowSignatureDialog(false)}
        onSignatureComplete={handleSignatureComplete}
        documentType="contractor_hire"
        documentData={data}
        signerName={data.clientName || ''}
        signerEmail={data.clientEmail || ''}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ContractorHireDocumentPreview;
